import { useT } from "../i18n/hooks.ts";
import type { SimulationPhase } from "../engine/simulateProject.ts";

const PHASE_LABEL_KEY: Record<
  SimulationPhase,
  "simulation.phaseRunoff" | "simulation.phaseRouting" | "simulation.phaseReport"
> = {
  runoff: "simulation.phaseRunoff",
  routing: "simulation.phaseRouting",
  report: "simulation.phaseReport",
};

const PHASE_ORDER: readonly SimulationPhase[] = ["runoff", "routing", "report"];

type StepStatus = "done" | "current" | "pending";

/**
 * The DYNWAVE case has no separate `runoff` phase — its runoff is interleaved
 * into `routing` (see `SimulationPhase`'s own comment). So a DYNWAVE run's
 * very first tick is `routing`, meaning the `runoff` chip must render as
 * `done` (the work IS happening, just under the routing driver) rather than
 * `pending` — otherwise the user watches a "pending" step for the entire run
 * that will never activate.
 */
function stepStatus(current: SimulationPhase, step: SimulationPhase): StepStatus {
  if (step === current) return "current";
  return PHASE_ORDER.indexOf(step) < PHASE_ORDER.indexOf(current) ? "done" : "pending";
}

/**
 * Real per-phase feedback while a simulation worker runs, instead of just
 * a spinner. `progress` is `null` only for the brief gap between clicking
 * Run and the worker's first progress message — rendering nothing then
 * avoids a bar frozen at 0%, which would look identical to a genuine stall.
 *
 * Two signals in one pill: (1) the three step chips (Escorrentía · Rutado ·
 * Informe) show WHICH phase is running with a ✓/●/○ marker each, so the
 * user sees the overall stage even when the current phase's fraction
 * resets between phases; (2) the thin bar below shows the CURRENT phase's
 * `fraction` verbatim — matching what the engine reports (routing/dynamic-
 * wave/runoff modules all deliver 0..1 monotonically per phase), no fake
 * overall-weight fudging.
 */
export function SimulationProgressBar({
  progress,
}: {
  progress: { phase: SimulationPhase; fraction: number } | null;
}) {
  const t = useT();
  if (!progress) return null;
  const percent = Math.round(progress.fraction * 100);
  const phaseLabel = t(PHASE_LABEL_KEY[progress.phase]);
  return (
    <div
      className="simulation-progress-bar"
      role="progressbar"
      aria-valuenow={percent}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={phaseLabel}
    >
      <div className="simulation-progress-bar-steps">
        {PHASE_ORDER.map((step) => {
          const status = stepStatus(progress.phase, step);
          return (
            <span
              key={step}
              className={`simulation-progress-bar-step simulation-progress-bar-step-${status}`}
            >
              <span className="simulation-progress-bar-step-marker" aria-hidden="true">
                {status === "done" ? (
                  <svg
                    viewBox="0 0 12 12"
                    width="10"
                    height="10"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="2.5 6.5 5 9 9.5 3.5" />
                  </svg>
                ) : null}
              </span>
              <span className="simulation-progress-bar-step-label">{t(PHASE_LABEL_KEY[step])}</span>
            </span>
          );
        })}
        <span className="simulation-progress-bar-percent">{percent}%</span>
      </div>
      <div className="simulation-progress-bar-track">
        <div className="simulation-progress-bar-track-fill" style={{ width: `${percent}%` }} />
      </div>
      {/* `role="progressbar"` alone is never re-announced as its value changes, so this visually-hidden `aria-live` region rounds down to the nearest 10% — announcing every tick verbatim would be unusable chatter. */}
      <span className="sr-only" aria-live="polite" aria-atomic="true">
        {phaseLabel} {Math.floor(percent / 10) * 10}%
      </span>
    </div>
  );
}
